import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

const BLANK_ITEM = { name: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0 };

function lineTotal(item) {
  const gross = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
  return gross - (gross * (Number(item.discountPercent) || 0)) / 100;
}

/**
 * Line-item editor for a Deal — lets a rep build the deal value bottom-up
 * from products/services instead of typing one flat number. Deal.value is
 * server-recomputed from these on save, so this component only ever pushes
 * the lineItems array up via onChange and displays whatever total comes back.
 */
export default function DealLineItems({ lineItems, currency, onChange }) {
  const [draft, setDraft] = useState(null);
  const items = lineItems || [];
  const total = items.reduce((sum, item) => sum + lineTotal(item), 0);

  const updateItem = (idx, patch) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const addItem = () => {
    if (!draft || !draft.name) return;
    onChange([...items, draft]);
    setDraft(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-semibold text-[#706E6B]">Line Items</label>
        <span className="text-[13px] font-bold text-[#080707] tabular-nums">{formatCurrency(total, currency)}</span>
      </div>

      {items.length === 0 && !draft && (
        <p className="text-[12px] text-[#706E6B]">No line items yet — Deal Value is a flat number until you add one.</p>
      )}

      {items.length > 0 && (
        <div className="border border-[#D8DDE6] rounded overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-right px-3 py-2 w-20">Qty</th>
                <th className="text-right px-3 py-2 w-28">Unit Price</th>
                <th className="text-right px-3 py-2 w-20">Disc %</th>
                <th className="text-right px-3 py-2 w-28">Total</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item._id || idx} className="border-t border-[#F3F2F2]">
                  <td className="px-3 py-2">
                    <input className="w-full bg-transparent outline-none font-medium" value={item.name} onChange={e => updateItem(idx, { name: e.target.value })} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" className="w-full bg-transparent outline-none text-right tabular-nums" value={item.quantity} onChange={e => updateItem(idx, { quantity: Number(e.target.value) })} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" step="0.01" className="w-full bg-transparent outline-none text-right tabular-nums" value={item.unitPrice} onChange={e => updateItem(idx, { unitPrice: Number(e.target.value) })} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" min="0" max="100" className="w-full bg-transparent outline-none text-right tabular-nums" value={item.discountPercent || 0} onChange={e => updateItem(idx, { discountPercent: Number(e.target.value) })} />
                  </td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums">{formatCurrency(lineTotal(item), currency)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => removeItem(idx)} className="text-[#706E6B] hover:text-[#BA0517]"><X size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <div className="flex items-center gap-2 p-3 border border-dashed border-[#0176D3] rounded bg-[#F4F6F9]">
          <input autoFocus placeholder="Item name" className="flex-1 h-9 px-2 border border-[#D8DDE6] rounded text-[12px]" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
          <input type="number" min="0" placeholder="Qty" className="w-16 h-9 px-2 border border-[#D8DDE6] rounded text-[12px] text-right" value={draft.quantity} onChange={e => setDraft({ ...draft, quantity: Number(e.target.value) })} />
          <input type="number" min="0" step="0.01" placeholder="Unit price" className="w-24 h-9 px-2 border border-[#D8DDE6] rounded text-[12px] text-right" value={draft.unitPrice} onChange={e => setDraft({ ...draft, unitPrice: Number(e.target.value) })} />
          <input type="number" min="0" max="100" placeholder="Disc %" className="w-16 h-9 px-2 border border-[#D8DDE6] rounded text-[12px] text-right" value={draft.discountPercent} onChange={e => setDraft({ ...draft, discountPercent: Number(e.target.value) })} />
          <button onClick={addItem} className="hover:bg-[#014486] h-9 px-3 bg-[#0176D3] text-white rounded text-[13px] font-semibold whitespace-nowrap shrink-0">Add</button>
          <button onClick={() => setDraft(null)} className="text-[#706E6B] hover:text-[#080707]"><X size={16} /></button>
        </div>
      )}

      {!draft && (
        <button onClick={() => setDraft({ ...BLANK_ITEM })} className="text-[12px] font-bold text-[#0176D3] hover:underline flex items-center gap-1">
          <Plus size={14} /> Add Line Item
        </button>
      )}
    </div>
  );
}
